// src/pages/admin/products/BatchInventoryManagement.jsx
import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import InlineLoader from '../../../components/ui/InlineLoader'
import ApiService from '../../../services/api'
import AuthService from '../../../services/authService'
import AppURLs from '../../../config/urls'
import { 
  Package, 
  AlertCircle, 
  RefreshCw,
  Calendar,
  Hash,
  TrendingDown,
  Box,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  ChevronDown,
  Filter,
  Search
} from 'lucide-react'

const BatchInventoryManagement = () => {
  // Get user role and branch - defined at component level
  const user = AuthService.getCurrentUser()
  const isManager = user?.role === 'manager'
  const isWarehouse = user?.role === 'warehouse'
  
  // Extract branch ID - try multiple possible structures
  const userBranchId = user?.branch?._id || user?.branch || user?.branchId || null
  
  console.log('🔍 User object:', user)
  console.log('🔍 Branch extraction:', {
    'branch._id': user?.branch?._id,
    'branch': user?.branch,
    'branchId': user?.branchId,
    'final userBranchId': userBranchId
  })

  const [activeTab, setActiveTab] = useState('product') // 'product' or 'raw_material'
  const [batches, setBatches] = useState([])
  const [allBatches, setAllBatches] = useState([]) // Store all batches for client-side filtering
  const [branches, setBranches] = useState([])
  const [branchFilter, setBranchFilter] = useState((isManager || isWarehouse) && userBranchId ? userBranchId : '')
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
    totalResults: 0
  })

  // Fetch branches
  const fetchBranches = async () => {
    try {
      const response = await ApiService.get(`${AppURLs.API}/branches`)
      if (response && response.success) {
        setBranches(response.data.branches || [])
      }
    } catch (error) {
      console.error('Error fetching branches:', error)
    }
  }

  // Fetch batch inventories
  const fetchBatches = async () => {
    try {
      setLoading(true)
      const params = {
        type: activeTab,
        page: 1,
        limit: 1000 // Fetch all batches for client-side filtering
      }

      const response = await ApiService.get(`${AppURLs.API}/batch-inventories`, params)

      if (response && response.success) {
        const allData = response.data.batches || []
        console.log('📦 Fetched batches:', allData.length)
        setAllBatches(allData)
        // Don't call applyFilters here - let useEffect handle it after state updates
      }
    } catch (error) {
      console.error('Error fetching batch inventories:', error)
    } finally {
      setLoading(false)
    }
  }

  // Apply client-side filters
  const applyFilters = (data) => {
    let filtered = [...data]

    console.log('=== APPLY FILTERS ===')
    console.log('User Role:', user?.role)
    console.log('Is Manager:', isManager)
    console.log('Is Warehouse:', isWarehouse)
    console.log('User Branch ID:', userBranchId)
    console.log('Branch Filter State:', branchFilter)
    console.log('Total batches before filter:', data.length)
    if (data[0]) {
      console.log('Sample batch:', {
        batchNumber: data[0].batchNumber,
        branch: data[0].branch,
        branchType: typeof data[0].branch
      })
    }

    // Filter by branch if branchFilter is set
    if (branchFilter) {
      console.log('🔍 Filtering by branch:', branchFilter)
      filtered = filtered.filter(batch => {
        const batchBranchId = batch.branch?._id || batch.branch
        const match = batchBranchId === branchFilter
        if (!match) {
          console.log(`  ❌ Filtered out ${batch.batchNumber}: batchBranch=${batchBranchId} !== filterBranch=${branchFilter}`)
        } else {
          console.log(`  ✅ Keeping ${batch.batchNumber}: batchBranch=${batchBranchId} === filterBranch=${branchFilter}`)
        }
        return match
      })
      console.log('✅ After branch filter:', filtered.length, 'batches')
    }

    // Filter by search query
    if (searchQuery) {
      console.log('🔍 Applying search filter:', searchQuery)
      filtered = filtered.filter(batch => 
        batch.batchNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        batch.grnNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        batch.product?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        batch.rawMaterial?.name?.toLowerCase().includes(searchQuery.toLowerCase())
      )
      console.log('✅ After search filter:', filtered.length, 'batches')
    }

    // Update pagination
    const totalResults = filtered.length
    const totalPages = Math.ceil(totalResults / pagination.limit)
    const startIndex = (pagination.page - 1) * pagination.limit
    const endIndex = startIndex + pagination.limit
    const paginatedData = filtered.slice(startIndex, endIndex)

    setBatches(paginatedData)
    setPagination(prev => ({
      ...prev,
      totalPages,
      totalResults
    }))
  }

  // Initialize - Fetch branches and data
  useEffect(() => {
    console.log('=== COMPONENT MOUNT ===')
    console.log('User:', user)
    console.log('User Role:', user?.role)
    console.log('User Branch ID:', userBranchId)
    console.log('Is Manager:', isManager)
    console.log('Is Warehouse:', isWarehouse)
    console.log('Initial branchFilter:', (isManager || isWarehouse) ? userBranchId : '')
    
    fetchBranches()
    fetchBatches()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Refetch when tab changes
  useEffect(() => {
    if (allBatches.length > 0 || batches.length > 0) {
      console.log('🔄 Tab changed to:', activeTab)
      fetchBatches()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  // Re-apply filters when filters or data change
  useEffect(() => {
    if (allBatches.length > 0) {
      console.log('🔄 Re-applying filters...', {
        branchFilter,
        searchQuery,
        totalBatches: allBatches.length
      })
      applyFilters(allBatches)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allBatches, branchFilter, searchQuery, pagination.page, pagination.limit])

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      'ACTIVE': { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Active' },
      'EXPIRED': { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Expired' },
      'LOW_STOCK': { color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle, label: 'Low Stock' },
      'DEPLETED': { color: 'bg-gray-100 text-gray-800', icon: XCircle, label: 'Depleted' }
    }

    const config = statusConfig[status] || statusConfig['ACTIVE']
    const Icon = config.icon

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </span>
    )
  }

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A'
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  // Check if expiry is near (within 30 days)
  const isExpiryNear = (expiryDate) => {
    if (!expiryDate) return false
    const daysUntilExpiry = Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24))
    return daysUntilExpiry <= 5 && daysUntilExpiry > 0
  }

  // Check if expired
  const isExpired = (expiryDate) => {
    if (!expiryDate) return false
    return new Date(expiryDate) < new Date()
  }

  // Get branch name by ID
  const getBranchName = (branchId) => {
    const branch = branches.find(b => b._id === branchId)
    return branch ? branch.name : 'N/A'
  }

  // Clear all filters
  const clearFilters = () => {
    const user = AuthService.getCurrentUser()
    const isManager = user?.role === 'manager'
    const isWarehouse = user?.role === 'warehouse'
    
    // Don't clear branch filter for manager/warehouse users
    if (!isManager && !isWarehouse) {
      setBranchFilter('')
    }
    setSearchQuery('')
    setPagination(prev => ({ ...prev, page: 1 }))
  }

  if (loading) {
    return <InlineLoader text="Loading batch inventories..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Batch Inventory Management</h1>
          <p className="text-gray-600 mt-1"></p>
        </div>
        <Button
          onClick={fetchBatches}
          variant="outline"
          className="flex items-center space-x-2"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </Button>
      </div>

      {/* Tabs */}
      <Card>
        <CardContent className="p-0">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('product')}
                className={`py-4 px-6 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'product'
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Package className="h-4 w-4" />
                  <span>Products</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('raw_material')}
                className={`py-4 px-6 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'raw_material'
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Box className="h-4 w-4" />
                  <span>Raw Materials</span>
                </div>
              </button>
            </nav>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          {/* Filter Header */}
          <div className="flex items-center space-x-2 mb-4">
            <Filter className="h-5 w-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Filter Batch Inventories</h3>
          </div>

          {/* Filter Controls */}
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center flex-1 w-full">
              {/* Search Bar */}
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search batch number..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value)
                    setPagination(prev => ({ ...prev, page: 1 }))
                  }}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                />
              </div>

              {/* Branch Filter Dropdown */}
              <div className="relative w-full sm:w-48">
                <select
                  value={branchFilter}
                  onChange={(e) => {
                    setBranchFilter(e.target.value)
                    setPagination(prev => ({ ...prev, page: 1 }))
                  }}
                  disabled={AuthService.getCurrentUser()?.role === 'manager' || AuthService.getCurrentUser()?.role === 'warehouse'}
                  className={`w-full appearance-none px-4 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 bg-white ${(AuthService.getCurrentUser()?.role === 'manager' || AuthService.getCurrentUser()?.role === 'warehouse') ? 'cursor-not-allowed opacity-60' : ''}`}
                >
                  <option value="">All Branches</option>
                  {branches.map((branch) => (
                    <option key={branch._id} value={branch._id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>

              {/* Info message for manager/warehouse users */}
              {(AuthService.getCurrentUser()?.role === 'manager' || AuthService.getCurrentUser()?.role === 'warehouse') && (
                <div className="flex items-center space-x-2 text-sm text-blue-600 bg-blue-50 px-3 py-2 rounded-md">
                  <AlertCircle className="h-4 w-4" />
                  <span>Showing batches from your assigned branch only</span>
                </div>
              )}
            </div>

            {/* Clear Filters Button */}
            {/* Show for: admin with filters OR manager/warehouse with search only */}
            {((!isManager && !isWarehouse && branchFilter) || searchQuery) && (
              <Button
                onClick={clearFilters}
                variant="outline"
                className="whitespace-nowrap w-full sm:w-auto"
              >
                Clear Filters
              </Button>
            )}
          </div>

          {/* Results Count */}
          <div className="mt-4 text-sm text-gray-600">
            Showing {batches.length} of {pagination.totalResults} batch inventories
          </div>
        </CardContent>
      </Card>

      {/* Batch List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="h-5 w-5 text-orange-600" />
            <span>Batch Inventory ({pagination.totalResults})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {batches.length > 0 ? (
            <div className="space-y-4">
              {/* Desktop Table */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b-2 border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Batch Number
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Branch
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {activeTab === 'product' ? 'Product' : 'Raw Material'}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        GRN Number
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quantity
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Expiry Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {batches.map((batch) => (
                      <tr key={batch._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <Hash className="h-4 w-4 text-gray-400" />
                            <span className="font-medium text-gray-900">{batch.batchNumber}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-gray-900">{getBranchName(batch.branch)}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <p className="font-medium text-gray-900">
                              {batch.rawMaterial?.name || batch.product?.name || 'N/A'}
                            </p>
                            <p className="text-sm text-gray-500">
                              {batch.rawMaterial?.code || batch.product?.sku || 'N/A'}
                            </p>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-gray-900">{batch.grnNumber || 'N/A'}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <p className="font-medium text-gray-900">
                              {batch.currentQuantity} / {batch.initialQuantity}
                            </p>
                            <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                              <div
                                className={`h-2 rounded-full ${
                                  (batch.currentQuantity / batch.initialQuantity) * 100 > 50
                                    ? 'bg-green-500'
                                    : (batch.currentQuantity / batch.initialQuantity) * 100 > 20
                                    ? 'bg-yellow-500'
                                    : 'bg-red-500'
                                }`}
                                style={{
                                  width: `${Math.min((batch.currentQuantity / batch.initialQuantity) * 100, 100)}%`
                                }}
                              />
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <div>
                              <p className={`text-sm ${
                                isExpired(batch.expiryDate) 
                                  ? 'text-red-600 font-medium' 
                                  : isExpiryNear(batch.expiryDate)
                                  ? 'text-yellow-600 font-medium'
                                  : 'text-gray-900'
                              }`}>
                                {formatDate(batch.expiryDate)}
                              </p>
                              {isExpiryNear(batch.expiryDate) && !isExpired(batch.expiryDate) && (
                                <p className="text-xs text-yellow-600">Expiring Soon</p>
                              )}
                              {isExpired(batch.expiryDate) && (
                                <p className="text-xs text-red-600">Expired</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(batch.status)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden space-y-4">
                {batches.map((batch) => (
                  <Card key={batch._id} className="shadow-sm">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-gray-900 flex items-center space-x-2">
                            <Hash className="h-4 w-4 text-gray-400" />
                            <span>{batch.batchNumber}</span>
                          </p>
                          <p className="text-sm text-gray-500 mt-1">
                            {batch.rawMaterial?.name || batch.product?.name || 'N/A'}
                          </p>
                        </div>
                        {getStatusBadge(batch.status)}
                      </div>

                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-gray-500">Branch</p>
                          <p className="text-gray-900 font-medium">{getBranchName(batch.branch)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">GRN Number</p>
                          <p className="text-gray-900 font-medium">{batch.grnNumber || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Expiry Date</p>
                          <p className={`font-medium ${
                            isExpired(batch.expiryDate) 
                              ? 'text-red-600' 
                              : isExpiryNear(batch.expiryDate)
                              ? 'text-yellow-600'
                              : 'text-gray-900'
                          }`}>
                            {formatDate(batch.expiryDate)}
                          </p>
                        </div>
                      </div>

                      <div>
                        <p className="text-sm text-gray-500 mb-1">Stock Level</p>
                        <div className="flex items-center space-x-2">
                          <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${
                                (batch.currentQuantity / batch.initialQuantity) * 100 > 50
                                  ? 'bg-green-500'
                                  : (batch.currentQuantity / batch.initialQuantity) * 100 > 20
                                  ? 'bg-yellow-500'
                                  : 'bg-red-500'
                              }`}
                              style={{
                                width: `${Math.min((batch.currentQuantity / batch.initialQuantity) * 100, 100)}%`
                              }}
                            />
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            {batch.currentQuantity}/{batch.initialQuantity}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between pt-4 border-t">
                  <p className="text-sm text-gray-700">
                    Showing page {pagination.page} of {pagination.totalPages}
                  </p>
                  <div className="flex space-x-2">
                    <Button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      disabled={pagination.page === 1}
                      variant="outline"
                      size="sm"
                    >
                      Previous
                    </Button>
                    <Button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      disabled={pagination.page === pagination.totalPages}
                      variant="outline"
                      size="sm"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Batch Inventories Found</h3>
              <p className="text-gray-600">
                There are no batch inventories for {activeTab === 'product' ? 'products' : 'raw materials'} yet.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default BatchInventoryManagement
